import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { checkIsActive } from '../../../../helpers'
import { SidebarMenuItem } from './SidebarMenuItem'
import { SidebarMenuItemWithSub } from './SidebarMenuItemWithSub'
import { SidebarSubmenuLink } from './SidebarSubmenuLink'
import dashboard from '../../../../assets/media/svg_supplier/dashboard.svg'
import POManagementIcon from '../../../../assets/media/svg_supplier/pom.svg'
import InventoryIcon from '../../../../assets/media/svg_supplier/inventory.svg'
import MyProfileIcon from '../../../../assets/media/svg_supplier/my profile.svg'
import master from '../../../../assets/media/svg_supplier/master.svg'
import CategoriesIcon from '../../../../assets/media/dashboard/catalogue.svg'
import BrandsIcon from '../../../../assets/media/dashboard/store.svg'
import MasterProductsIcon from '../../../../assets/media/dashboard/product-inventory.svg'

const MENU = {
  master: '/master',
  inventory: '/inventory',
} as const

type MenuKey = (typeof MENU)[keyof typeof MENU]

const getActiveMenuKey = (pathname: string): MenuKey | null => {
  if (
    checkIsActive(pathname, MENU.master) ||
    checkIsActive(pathname, '/products/master-products')
  ) {
    return MENU.master
  }
  if (checkIsActive(pathname, MENU.inventory)) {
    return MENU.inventory
  }
  return null
}

const SidebarMenuMain = () => {
  const intl = useIntl()
  const { pathname } = useLocation()
  const [openMenu, setOpenMenu] = useState<MenuKey | null>(() => getActiveMenuKey(pathname))

  useEffect(() => {
    setOpenMenu(getActiveMenuKey(pathname))
  }, [pathname])

  const handleToggleMenu = (menuKey: MenuKey) => {
    setOpenMenu((current) => (current === menuKey ? null : menuKey))
  }

  const handleCloseSubmenus = () => {
    setOpenMenu(null)
  }

  return (
    <>
      <SidebarMenuItem
        to='/dashboard'
        icon={dashboard}
        title={intl.formatMessage({ id: 'Menu.Dashboard' })}
        onClick={handleCloseSubmenus}
      />
      <SidebarMenuItem
        to='/profile-management/manage-profile'
        icon={MyProfileIcon}
        title={intl.formatMessage({ id: 'Menu.MyProfile' })}
        onClick={handleCloseSubmenus}
      />
      <SidebarMenuItem
        to='/purchase-order/supplier-ledger'
        icon={POManagementIcon}
        title={intl.formatMessage({ id: 'Menu.POManagement' })}
        onClick={handleCloseSubmenus}
      />
      <SidebarMenuItemWithSub
        to={MENU.master}
        icon={master}
        title={intl.formatMessage({ id: 'Menu.Master' })}
        defaultPath='/master/categories'
        isOpen={openMenu === MENU.master}
        onToggle={() => handleToggleMenu(MENU.master)}
      >
        <SidebarSubmenuLink
          to='/master/categories'
          activePath='/master/categories'
          pathname={pathname}
          icon={CategoriesIcon}
          label={intl.formatMessage({ id: 'Menu.Categories', defaultMessage: 'Category' })}
          isFirst
        />
        <SidebarSubmenuLink
          to='/master/brands'
          activePath='/master/brands'
          pathname={pathname}
          icon={BrandsIcon}
          label={intl.formatMessage({ id: 'Menu.Brands', defaultMessage: 'Brands' })}
        />
        <SidebarSubmenuLink
          to='/products/master-products'
          activePath='/products/master-products'
          pathname={pathname}
          icon={MasterProductsIcon}
          label={intl.formatMessage({ id: 'Menu.MasterProducts', defaultMessage: 'Master Products' })}
        />
        <SidebarSubmenuLink
          to='/master/product-variants'
          activePath='/master/product-variants'
          pathname={pathname}
          icon={master}
          label={intl.formatMessage({ id: 'Menu.ProductVariants', defaultMessage: 'Product Variants' })}
        />
      </SidebarMenuItemWithSub>
      <SidebarMenuItemWithSub
        to={MENU.inventory}
        icon={InventoryIcon}
        title={intl.formatMessage({ id: 'Menu.Inventory' })}
        defaultPath='/inventory/goods-in-warehouse'
        isOpen={openMenu === MENU.inventory}
        onToggle={() => handleToggleMenu(MENU.inventory)}
      >
        <SidebarSubmenuLink
          to='/inventory/goods-in-warehouse'
          activePath='/inventory/goods-in-warehouse'
          pathname={pathname}
          icon={InventoryIcon}
          label={intl.formatMessage({ id: 'Menu.GoodsInWarehouse', defaultMessage: 'Goods in Warehouse' })}
        />
      </SidebarMenuItemWithSub>
    </>
  )
}

export { SidebarMenuMain }
